/**
 * The activity feed's own arithmetic: a sentence for any row, and — for an
 * edit or a delete — the before/after table underneath it.
 *
 * Deliberately not in `src/screens/Activity.tsx`. That file has JSX in it,
 * which needs `--jsx` configured to type-check, and `tests/` are compiled
 * under `tsconfig.worker.json` (`server/`, `functions/`, `tests/`, `scripts/`,
 * `worker/`), which has none — the same reason `src/lib/settle.ts` and
 * `src/lib/format.ts` hold their screens' `describe*` functions rather than
 * the screens holding them. A test can import this module directly; it could
 * not import the screen.
 *
 * **The line is written from the feed row's own `data`, not from a lookup of
 * the thing it names.** That is what the column is for: a
 * `transaction.created` row still reads correctly after the expense has been
 * corrected or removed, because the feed says what happened at the time and
 * the ledger says what is true now.
 *
 * **Editing a two-month-old expense silently changes what people owe each
 * other**, and a one-line "Ada corrected Groceries" does not say how.
 * `activityDetail` is the other half of that: it reads the same `before` /
 * `after` / `snapshot` JSON `server/transactions.ts` writes into `data_json`
 * and turns the fields that actually moved into rows a table can draw, so an
 * edit and a delete both read clearly rather than merely being mentioned.
 */

import type { ActivityEntry } from '../api'
import { categoryFor } from './categories'
import { formatDated, formatDay, formatMoney } from './format'

function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key]
  return typeof value === 'string' ? value : null
}

function readCents(data: Record<string, unknown>, key: string): number | null {
  const value = data[key]
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

/** One feed row as a sentence, and nothing about how it looks. */
export function describeActivity(entry: ActivityEntry, currency = 'USD'): string {
  const who = entry.actorDisplayName ?? 'Somebody'
  const what = readString(entry.data, 'description')
  const cents = readCents(entry.data, 'amount_cents')
  const amount = cents === null ? null : formatMoney(cents, currency)
  // What a settlement was for, when it was tagged to a project.
  const settledProject = readString(entry.data, 'project_name')
  const forProject = settledProject ? ` for ${settledProject}` : ''

  switch (entry.kind) {
    case 'group.created':
      return `${who} started ${readString(entry.data, 'name') ?? entry.groupName}`
    case 'group.updated':
      return `${who} changed the group's details`
    case 'group.archived':
      return `${who} archived the group`
    case 'group.unarchived':
      return `${who} brought the group back`
    // Its own sentence, because the plan everybody in the group is looking at
    // has just changed shape and this is where somebody finds out why.
    case 'group.simplify_on':
      return `${who} turned on simplified settling up`
    case 'group.simplify_off':
      return `${who} turned off simplified settling up`
    case 'member.joined':
      return `${who} joined`
    case 'member.added':
      return `${who} added somebody`
    case 'member.left':
      return `${who} left`
    case 'member.removed':
      return `${who} removed somebody`
    case 'member.rejoined':
      return `${who} came back`
    // Somebody at the table whose email nobody has. The name is in the row's
    // own data, like everything else here, so the line still reads after they
    // have claimed the row and been renamed by whatever they signed in with.
    case 'member.placeholder_added':
      return `${who} added ${readString(entry.data, 'display_name') ?? 'somebody'}, who has no account yet`
    // The other end of that: the link was followed, and the row a group has
    // been splitting expenses with is now somebody's own account. It moved no
    // balance, which is the whole design, so the line carries no amount.
    case 'member.claimed':
      return `${readString(entry.data, 'display_name') ?? who} claimed their place`
    case 'invite.created':
      return `${who} invited ${
        readString(entry.data, 'display_name') ?? readString(entry.data, 'email') ?? 'somebody'
      }`
    case 'invite.revoked':
      return `${who} revoked an invite link`
    case 'transaction.created': {
      // A settlement is a transaction like any other, and "added Payment to
      // Ben, $30.00" is not what happened: nobody added a payment, somebody
      // made one. The row's own `kind` says which, so the feed can say it
      // without joining back to a table.
      const added =
        readString(entry.data, 'kind') === 'repayment'
          ? amount
            ? `${who} recorded a payment of ${amount}`
            : `${who} recorded a payment`
          : amount && what
            ? `${who} added ${what}, ${amount}`
            : `${who} added an expense`
      // A repeating expense whose project was archived before it fell due goes
      // in with no project, and this is the one place anybody would learn why.
      const dropped = readString(entry.data, 'dropped_project_name')
      if (dropped) return `${added}, but not in ${dropped}, which is archived`
      const project = readString(entry.data, 'project_name')
      if (!project) return added
      // A payment is *for* a project — it settled what was spent there — and
      // an expense is *in* one.
      return readString(entry.data, 'kind') === 'repayment'
        ? `${added} for ${project}`
        : `${added} in ${project}`
    }
    // The other half of that: the person paid saying it arrived. It moved no
    // balance — the balance moved when the payment was recorded — so the line
    // carries no amount of its own beyond the one being agreed to.
    case 'settlement.confirmed':
      return amount
        ? `${who} confirmed a payment of ${amount}${forProject} arrived`
        : `${who} confirmed a payment${forProject} arrived`
    // And the answer nobody wants to have to give: the person paid saying the
    // money never turned up. It is a removal — the payment is off the ledger
    // and the debt is back — so the amount is the amount that went back, not
    // an amount that moved.
    case 'settlement.rejected':
      return amount
        ? `${who} said a payment of ${amount}${forProject} never arrived, so it was removed`
        : `${who} said a payment${forProject} never arrived, so it was removed`
    case 'transaction.updated':
      return what ? `${who} corrected ${what}` : `${who} corrected an expense`
    // The same distinction `transaction.created` makes, at the other end.
    // Nobody "removes a payment": a payment that is taken back was cancelled,
    // and the row's own `kind` says which of the two this was without a join
    // back to a table the row may since have left.
    case 'transaction.deleted':
      if (readString(entry.data, 'kind') === 'repayment') {
        return amount
          ? `${who} cancelled a payment of ${amount}${forProject}`
          : `${who} cancelled a payment${forProject}`
      }
      return what ? `${who} removed ${what}` : `${who} removed an expense`
    // A rule, not an expense: nothing was spent when this row was written, and
    // the amount on it is what *each* occurrence will be. "every month" is the
    // whole of the recurrence rule — there is only one frequency — so it can
    // be said in the sentence rather than looked up.
    case 'recurring.created':
      return amount && what
        ? `${who} set up ${what}, ${amount} every month`
        : `${who} set up a repeating expense`
    case 'recurring.updated':
      return what ? `${who} changed ${what}` : `${who} changed a repeating expense`
    case 'recurring.cancelled':
      return what ? `${who} stopped ${what} repeating` : `${who} stopped a repeating expense`
    // One month off, asked for ahead of the date. The date is in the row's own
    // data, because by the time anybody reads this line the rule has moved on
    // to the next one.
    case 'recurring.skipped': {
      const occursOn = readString(entry.data, 'occurs_on')
      const skipped = what ? `${what}` : 'a repeating expense'
      return occursOn ? `${who} skipped ${skipped} on ${formatDated(occursOn)}` : `${who} skipped ${skipped}`
    }
    // A project is a label on some of the group's transactions, so none of
    // these lines carries an amount: making, renaming or archiving one moves
    // nobody's balance. The name is the row's own, as it was at the time.
    case 'project.created': {
      const name = readString(entry.data, 'name')
      const count = readCents(entry.data, 'transaction_count') ?? 0
      const made = name ? `${who} started the project ${name}` : `${who} started a project`
      return count > 0 ? `${made}, with ${count} ${count === 1 ? 'expense' : 'expenses'} in it` : made
    }
    case 'project.renamed': {
      const name = readString(entry.data, 'name')
      const previous = readString(entry.data, 'previous_name')
      if (name && previous) return `${who} renamed the project ${previous} to ${name}`
      return name ? `${who} renamed a project to ${name}` : `${who} renamed a project`
    }
    case 'project.archived':
      return `${who} archived the project ${readString(entry.data, 'name') ?? ''}`.trimEnd()
    case 'project.unarchived':
      return `${who} reopened the project ${readString(entry.data, 'name') ?? ''}`.trimEnd()
    // Filing something in a project, moving it to another or taking it out.
    // No amount either: the expense's money is exactly where it was. A bulk
    // attach or detach is one row with a count instead of a description.
    case 'transaction.project_changed': {
      const to = readString(entry.data, 'project_name')
      const from = readString(entry.data, 'previous_project_name')
      const count = readCents(entry.data, 'transaction_count')
      const subject =
        count !== null
          ? `${count} ${count === 1 ? 'expense' : 'expenses'}`
          : readString(entry.data, 'kind') === 'repayment'
            ? 'a payment'
            : (what ?? 'an expense')
      if (to && from) return `${who} moved ${subject} from ${from} to ${to}`
      if (to) return `${who} put ${subject} in ${to}`
      if (from) return `${who} took ${subject} out of ${from}`
      return `${who} changed the project of ${subject}`
    }
    case 'receipt.added':
      // The name is read out of the row's own `data_json`, like everything
      // else here: the receipt it names may since have been deleted, and the
      // feed says what happened rather than what is still there.
      return `${who} attached ${readString(entry.data, 'original_name') ?? 'a receipt'}`
    case 'receipt.removed':
      return `${who} removed ${readString(entry.data, 'original_name') ?? 'a receipt'}`
    // A receipt taken at the till and put on an expense later. Two words
    // rather than one, because "attached" is what an upload says and these
    // rows are about a receipt that was already there.
    case 'receipt.filed':
      return `${who} filed ${readString(entry.data, 'original_name') ?? 'a receipt'} on an expense`
    case 'receipt.unfiled':
      return `${who} took ${readString(entry.data, 'original_name') ?? 'a receipt'} off an expense`
    case 'comment.added':
      return `${who} left a comment`
    default:
      return `${who} — ${entry.kind}`
  }
}

// ---------------------------------------------------------------------------
// The before/after — `transaction.updated` and `transaction.deleted` only.
// ---------------------------------------------------------------------------

export type SnapshotLine = { userId: string; side: 'paid' | 'owes'; amountCents: number }

function readSnapshot(data: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = data[key]
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function snapshotLines(snapshot: Record<string, unknown> | null): SnapshotLine[] {
  const value = snapshot?.lines
  if (!Array.isArray(value)) return []
  const lines: SnapshotLine[] = []
  for (const raw of value) {
    if (raw === null || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    if (
      typeof row.user_id === 'string' &&
      (row.side === 'paid' || row.side === 'owes') &&
      typeof row.amount_cents === 'number'
    ) {
      lines.push({ userId: row.user_id, side: row.side, amountCents: row.amount_cents })
    }
  }
  return lines
}

function snapshotCurrency(snapshot: Record<string, unknown> | null, fallback: string): string {
  const value = snapshot?.currency
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/** Every field a diff row might be about, in the order it is shown. */
const FIELD_LABELS: Record<string, string> = {
  kind: 'Type',
  description: 'Description',
  category: 'Category',
  amount_cents: 'Amount',
  occurred_on: 'Date',
  note: 'Note',
  split_mode: 'Split',
  confirmed_at: 'Confirmation',
}
const FIELD_ORDER = Object.keys(FIELD_LABELS)

function formatSnapshotField(
  field: string,
  snapshot: Record<string, unknown> | null,
  currency: string,
): string {
  const value = snapshot?.[field] ?? null
  switch (field) {
    case 'amount_cents':
      return typeof value === 'number' ? formatMoney(value, currency) : '—'
    case 'occurred_on':
      return typeof value === 'string' ? formatDay(value) : '—'
    case 'confirmed_at':
      return value ? 'Confirmed' : 'Not confirmed'
    case 'note':
      return typeof value === 'string' && value.trim() !== '' ? value : '—'
    case 'category':
      return typeof value === 'string' && value.trim() !== ''
        ? (categoryFor(value)?.label ?? value)
        : 'Uncategorised'
    default:
      return typeof value === 'string' && value.length > 0 ? value : '—'
  }
}

export type ActivityDiffRow = { field: string; label: string; before: string; after: string }

export type ActivityDetail = {
  /** Only the fields that actually changed (an edit), or every field there was (a delete). */
  rows: ActivityDiffRow[]
  /** The split, before and after. Empty on both sides when the split did not move. */
  linesBefore: SnapshotLine[]
  linesAfter: SnapshotLine[]
  currency: string
}

/**
 * The JSON snapshot an edit or a removal leaves in the feed, as a
 * before/after a person can actually read. `null` for every other kind of
 * row, and for one of these if it has nothing to show — `changed` naming only `split_json`
 * is the one real case: the money and the lines are unchanged, only what was
 * *typed* to arrive at them is, and there is no before/after in cents to draw
 * for that.
 */
export function activityDetail(entry: ActivityEntry): ActivityDetail | null {
  if (entry.kind === 'transaction.updated') {
    const before = readSnapshot(entry.data, 'before')
    // Rows written before this ticket shipped have no `after` snapshot — only
    // `before` and the *new* description/amount at the top level, which is
    // what an edit has always carried. Falling back to those two keeps an old
    // row's detail panel honest about what it does and does not know, rather
    // than showing "—" for fields a newer row would have.
    const after =
      readSnapshot(entry.data, 'after') ??
      ({ description: entry.data.description, amount_cents: entry.data.amount_cents } as Record<
        string,
        unknown
      >)
    const changedRaw = entry.data.changed
    const changed = Array.isArray(changedRaw)
      ? changedRaw.filter((field): field is string => typeof field === 'string')
      : []
    const currency = snapshotCurrency(after, snapshotCurrency(before, 'USD'))

    const rows = FIELD_ORDER.filter((field) => changed.includes(field)).map((field) => ({
      field,
      label: FIELD_LABELS[field],
      before: formatSnapshotField(field, before, currency),
      after: formatSnapshotField(field, after, currency),
    }))
    const linesChanged = changed.includes('lines')
    if (rows.length === 0 && !linesChanged) return null

    return {
      rows,
      linesBefore: linesChanged ? snapshotLines(before) : [],
      linesAfter: linesChanged ? snapshotLines(after) : [],
      currency,
    }
  }

  // A rejected settlement is a delete with a different name on it — same
  // snapshot, same "everything about the row, then Removed" table — so it
  // reads through this branch rather than a copy of it.
  if (entry.kind === 'transaction.deleted' || entry.kind === 'settlement.rejected') {
    const snapshot = readSnapshot(entry.data, 'snapshot')
    if (!snapshot) return null
    const currency = snapshotCurrency(snapshot, 'USD')
    // Every deleted transaction had a kind, a description, an amount, an
    // occurred_on and a split; not every one had a category, a note or a
    // confirmation, and rows for those would be "— → Removed" on every plain
    // expense in the group, which is not a fact worth a line.
    const fields = FIELD_ORDER.filter((field) => {
      if (field === 'category' || field === 'note') return snapshot[field] != null
      if (field === 'confirmed_at') return Boolean(snapshot[field])
      return true
    })
    const rows = fields.map((field) => ({
      field,
      label: FIELD_LABELS[field],
      before: formatSnapshotField(field, snapshot, currency),
      after: 'Removed',
    }))

    return { rows, linesBefore: snapshotLines(snapshot), linesAfter: [], currency }
  }

  return null
}

/** One line of a split, as a sentence — "Ada paid $120.00" or "usr_ada paid $120.00"
 * when there is no name for it, exactly as the rest of this app falls back to an id. */
export function describeSnapshotLine(
  line: SnapshotLine,
  currency: string,
  nameOf?: Map<string, string>,
): string {
  const who = nameOf?.get(line.userId) ?? line.userId
  const verb = line.side === 'paid' ? 'paid' : 'owed'
  return `${who} ${verb} ${formatMoney(line.amountCents, currency)}`
}
